import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert, Badge, Card, Col, Descriptions, Empty, Row, Select, Space, Statistic,
  Table, Tabs, Tag, Typography, Button, Switch, message,
} from 'antd';
import {
  BugOutlined, ReloadOutlined, ThunderboltOutlined, ApiOutlined,
} from '@ant-design/icons';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { inverter_api } from '../api/inverter-api';
import { buildDebugStreamUrl } from '../api/debug-stream';
import {
  mapSoftDebugFromMqtt, fmt, FAULT_DEFINITIONS,
} from '../utils/softDebugMqtt';

const { Title, Text } = Typography;

function Metric({ title, value, suffix }) {
  return (
    <Card size="small" styles={{ body: { padding: '14px 16px' } }}>
      <Statistic
        title={<span style={{ fontSize: 14, fontWeight: 600 }}>{title}</span>}
        value={value ?? '—'}
        suffix={suffix ? <span style={{ fontSize: 18, fontWeight: 700 }}>{suffix}</span> : undefined}
        valueStyle={{ fontSize: 28, fontWeight: 700, lineHeight: 1.2, color: '#111' }}
      />
    </Card>
  );
}

function BoldVal({ children }) {
  return <Text strong style={{ fontSize: 20 }}>{children}</Text>;
}

function FlagTag({ ok, label }) {
  return (
    <Tag color={ok ? 'green' : 'default'} style={{ fontSize: 14, padding: '2px 10px', fontWeight: 600 }}>
      {label}
    </Tag>
  );
}

function SoftDebugPanel({ view }) {
  if (!view) {
    return <Empty description="Chọn thiết bị để xem SoftDebug (MQTT)" />;
  }

  const { meta, dashboard, realtime, faults, dab, inverter, mppt, pll } = view;

  const faultColumns = [
    { title: 'CODE', dataIndex: 'softdebug', width: 80 },
    { title: 'MQTT', dataIndex: 'code', width: 140 },
    {
      title: 'SEVERITY',
      dataIndex: 'severity',
      width: 100,
      render: (v) => {
        const color = { critical: 'red', error: 'volcano', warning: 'orange', info: 'blue' }[v] || 'default';
        return <Tag color={color}>{v}</Tag>;
      },
    },
    { title: 'NAME', dataIndex: 'title' },
    {
      title: 'STATUS',
      width: 90,
      render: () => <Tag color="red">ACTIVE</Tag>,
    },
  ];

  const catalogColumns = [
    { title: 'Bit', dataIndex: 'bit', width: 50 },
    { title: 'SoftDebug', dataIndex: 'softdebug', width: 90 },
    { title: 'MQTT code', dataIndex: 'code', width: 160 },
    { title: 'Severity', dataIndex: 'severity', width: 100 },
    { title: 'Mô tả', dataIndex: 'title' },
  ];

  const tabItems = [
    {
      key: 'dashboard',
      label: 'Dashboard',
      children: (
        <Space direction="vertical" size={16} style={{ width: '100%' }}>
          <Row gutter={[12, 12]}>
            <Col xs={12} md={6}><Metric title="Mode MCU" value={dashboard.systemModeLabel} /></Col>
            <Col xs={12} md={6}><Metric title="DAB" value={dashboard.dabEnabled ? 'ON' : 'OFF'} /></Col>
            <Col xs={12} md={6}><Metric title="DAB allowed" value={dashboard.dabAllowed ? 'YES' : 'NO'} /></Col>
            <Col xs={12} md={6}><Metric title="HV Bus" value={fmt(dashboard.hvBusVoltage, 1)} suffix="V" /></Col>
          </Row>
          <Row gutter={[12, 12]}>
            <Col xs={12} md={6}><Metric title="PV Power" value={fmt(dashboard.pvPower, 1)} suffix="W" /></Col>
            <Col xs={12} md={6}><Metric title="Battery Power" value={fmt(dashboard.batteryPower, 1)} suffix="W" /></Col>
            <Col xs={12} md={6}><Metric title="Grid Power" value={fmt(dashboard.gridPower, 1)} suffix="W" /></Col>
            <Col xs={12} md={6}><Metric title="Load Power" value={fmt(dashboard.loadPower, 1)} suffix="W" /></Col>
          </Row>
          <Row gutter={[12, 12]}>
            <Col xs={12} md={6}><Metric title="PV V" value={fmt(dashboard.pvVoltage, 1)} suffix="V" /></Col>
            <Col xs={12} md={6}><Metric title="Battery V" value={fmt(dashboard.batteryVoltage, 2)} suffix="V" /></Col>
            <Col xs={12} md={6}><Metric title="Grid V" value={fmt(dashboard.gridVoltage, 1)} suffix="V" /></Col>
            <Col xs={12} md={6}><Metric title="Grid f" value={fmt(dashboard.gridFrequency, 2)} suffix="Hz" /></Col>
          </Row>
          <Alert
            type={dashboard.hardFaults.length ? 'error' : dashboard.warnings.length ? 'warning' : 'success'}
            showIcon
            message={
              dashboard.hardFaults.length
                ? `Fault: ${dashboard.hardFaults.map((f) => f.softdebug).join(', ')}`
                : dashboard.warnings.length
                  ? `Warning: ${dashboard.warnings.map((f) => f.softdebug).join(', ')}`
                  : 'Không có fault / warning active'
            }
          />
        </Space>
      ),
    },
    {
      key: 'realtime',
      label: 'Realtime',
      children: (
        <Row gutter={[16, 16]}>
          <Col xs={24} lg={12}>
            <Card title="Fast group" size="small">
              <Descriptions column={1} size="small" bordered>
                <Descriptions.Item label="PV V / I"><BoldVal>{fmt(realtime.pvVoltage, 1, 'V')} / {fmt(realtime.pvCurrent, 2, 'A')}</BoldVal></Descriptions.Item>
                <Descriptions.Item label="48V (Battery)"><BoldVal>{fmt(realtime.batteryVoltage, 2, 'V')}</BoldVal></Descriptions.Item>
                <Descriptions.Item label="HV Bus"><BoldVal>{fmt(realtime.hvBusVoltage, 1, 'V')}</BoldVal></Descriptions.Item>
                <Descriptions.Item label="Grid V / I"><BoldVal>{fmt(realtime.gridVoltage, 1, 'V')} / {fmt(realtime.gridCurrent, 2, 'A')}</BoldVal></Descriptions.Item>
              </Descriptions>
            </Card>
          </Col>
          <Col xs={24} lg={12}>
            <Card title="Slow group" size="small">
              <Descriptions column={1} size="small" bordered>
                <Descriptions.Item label="PV / Bat / Grid / Load W">
                  <BoldVal>
                    {fmt(realtime.pvPower, 0)} / {fmt(realtime.batteryPower, 0)} / {fmt(realtime.gridPower, 0)} / {fmt(realtime.loadPower, 0)}
                  </BoldVal>
                </Descriptions.Item>
                <Descriptions.Item label="Grid f (PLL / ZC)">
                  <BoldVal>{fmt(realtime.gridFrequency, 2, 'Hz')} / {fmt(realtime.gridFrequencyZc, 2, 'Hz')}</BoldVal>
                </Descriptions.Item>
              </Descriptions>
            </Card>
          </Col>
          <Col span={24}>
            <Card title="ADC raw (BOARD SoftDebug @4..20)" size="small">
              <Descriptions column={{ xs: 1, sm: 2, md: 3 }} size="small" bordered>
                {Object.entries(realtime.adc || {}).map(([k, v]) => (
                  <Descriptions.Item key={k} label={k}><BoldVal>{v ?? '—'}</BoldVal></Descriptions.Item>
                ))}
              </Descriptions>
            </Card>
          </Col>
        </Row>
      ),
    },
    {
      key: 'fault',
      label: (
        <span>
          Fault{' '}
          {faults.active.length > 0 ? <Badge count={faults.active.length} size="small" /> : null}
        </span>
      ),
      children: (
        <Space direction="vertical" size={16} style={{ width: '100%' }}>
          <Descriptions bordered size="small" column={{ xs: 1, md: 3 }}>
            <Descriptions.Item label="faultMask"><BoldVal>0x{(Number(faults.mask) || 0).toString(16).toUpperCase()}</BoldVal></Descriptions.Item>
            <Descriptions.Item label="faultLastId"><BoldVal>{faults.lastId == null || faults.lastId === 255 ? '—' : faults.lastId}</BoldVal></Descriptions.Item>
            <Descriptions.Item label="faultValue"><BoldVal>{fmt(faults.value, 3)}</BoldVal></Descriptions.Item>
          </Descriptions>
          <Table
            size="small"
            rowKey="code"
            pagination={false}
            dataSource={faults.active}
            columns={faultColumns}
            locale={{ emptyText: 'Không có fault active' }}
          />
          <Card title="Catalog SoftDebug ↔ MQTT" size="small">
            <Table
              size="small"
              rowKey="bit"
              pagination={false}
              dataSource={FAULT_DEFINITIONS}
              columns={catalogColumns}
            />
          </Card>
        </Space>
      ),
    },
    {
      key: 'dab',
      label: 'DAB',
      children: (
        <Row gutter={[12, 12]}>
          <Col xs={12} md={6}><Metric title="Enable" value={dab.enabled ? 'ON' : 'OFF'} /></Col>
          <Col xs={12} md={6}><Metric title="Allowed" value={dab.allowed ? 'YES' : 'NO'} /></Col>
          <Col xs={12} md={6}><Metric title="Mode" value={dab.modeLabel} /></Col>
          <Col xs={12} md={6}><Metric title="Phase" value={fmt(dab.phaseDeg, 2)} suffix="°" /></Col>
          <Col xs={12} md={6}><Metric title="LV V" value={fmt(dab.lvVoltage, 2)} suffix="V" /></Col>
          <Col xs={12} md={6}><Metric title="LV I raw" value={fmt(dab.lvCurrentRaw, 0)} /></Col>
          <Col xs={12} md={6}><Metric title="HV V" value={fmt(dab.hvVoltage, 1)} suffix="V" /></Col>
          <Col xs={12} md={6}><Metric title="HV I" value={fmt(dab.hvCurrentA, 3)} suffix="A" /></Col>
          <Col xs={12} md={6}><Metric title="HV I raw" value={fmt(dab.hvCurrentRaw, 0)} /></Col>
          <Col xs={12} md={6}><Metric title="HV Power" value={fmt(dab.hvPower, 1)} suffix="W" /></Col>
          <Col xs={12} md={6}><Metric title="HV Target" value={fmt(dab.hvTargetV, 1)} suffix="V" /></Col>
          <Col xs={12} md={6}><Metric title="V err" value={fmt(dab.vErrV, 2)} suffix="V" /></Col>
          <Col xs={24} md={12}><Metric title="Chiều dòng" value={dab.direction} /></Col>
          <Col xs={24} md={12}><Metric title="Target cmd" value={fmt(dab.targetCmdV, 1)} suffix="V" /></Col>
        </Row>
      ),
    },
    {
      key: 'inverter',
      label: 'Inverter',
      children: (
        <Space direction="vertical" size={16} style={{ width: '100%' }}>
          <Card title="Công suất PV" size="small">
            <Row gutter={[12, 12]}>
              <Col xs={12} md={6}><Metric title="U PV" value={fmt(inverter.pvVoltage, 1)} suffix="V" /></Col>
              <Col xs={12} md={6}><Metric title="I PV" value={fmt(inverter.pvCurrent, 2)} suffix="A" /></Col>
              <Col xs={12} md={6}><Metric title="P PV" value={fmt(inverter.pvPower, 1)} suffix="W" /></Col>
              <Col xs={12} md={6}><Metric title="DC Bus" value={fmt(inverter.hvBusVoltage, 1)} suffix="V" /></Col>
            </Row>
          </Card>

          <Card title="Công suất DAB HV" size="small">
            <Row gutter={[12, 12]}>
              <Col xs={12} md={6}><Metric title="U HV" value={fmt(dab.hvVoltage, 1)} suffix="V" /></Col>
              <Col xs={12} md={6}><Metric title="I HV" value={fmt(dab.hvCurrentA, 3)} suffix="A" /></Col>
              <Col xs={12} md={6}><Metric title="P HV" value={fmt(dab.hvPower, 1)} suffix="W" /></Col>
              <Col xs={12} md={6}><Metric title="I HV raw" value={fmt(dab.hvCurrentRaw, 0)} /></Col>
            </Row>
          </Card>

          <Card title="Công suất DAB LV" size="small">
            <Row gutter={[12, 12]}>
              <Col xs={12} md={6}><Metric title="U LV" value={fmt(dab.lvVoltage, 2)} suffix="V" /></Col>
              <Col xs={12} md={6}><Metric title="I LV (est.)" value={fmt(dab.lvCurrentA, 2)} suffix="A" /></Col>
              <Col xs={12} md={6}><Metric title="P LV" value={fmt(dab.lvPower, 1)} suffix="W" /></Col>
              <Col xs={12} md={6}><Metric title="I LV raw" value={fmt(dab.lvCurrentRaw, 0)} /></Col>
            </Row>
          </Card>

          <Card title="Công suất hoà lưới (AC)" size="small">
            <Row gutter={[12, 12]}>
              <Col xs={12} md={6}><Metric title="U lưới (Vac)" value={fmt(inverter.vacRms, 1)} suffix="V" /></Col>
              <Col xs={12} md={6}><Metric title="I lưới (Iac)" value={fmt(inverter.iacRms, 2)} suffix="A" /></Col>
              <Col xs={12} md={6}><Metric title="P hoà lưới" value={fmt(inverter.acActivePower, 1)} suffix="W" /></Col>
              <Col xs={12} md={6}><Metric title="S / cosφ" value={`${fmt(inverter.acApparentPower, 0)} / ${fmt(inverter.acCosPhi, 3)}`} /></Col>
              <Col xs={12} md={6}><Metric title="f PLL" value={fmt(inverter.gridFreqPll, 2)} suffix="Hz" /></Col>
              <Col xs={12} md={6}><Metric title="f ZC" value={fmt(inverter.gridFreqZc, 2)} suffix="Hz" /></Col>
              <Col xs={12} md={6}><Metric title="HERIC f" value={fmt(inverter.hericFrequency, 1)} suffix="Hz" /></Col>
              <Col xs={12} md={6}><Metric title="Load Power" value={fmt(dashboard.loadPower, 1)} suffix="W" /></Col>
            </Row>
          </Card>

          <Card title="InvGrid / Relay / Arbiter" size="small">
            <Space wrap>
              <Tag color="blue" style={{ fontSize: 14, fontWeight: 600 }}>{inverter.invGridLabel}</Tag>
              <Tag style={{ fontSize: 14, fontWeight: 600 }}>{inverter.powerArbiterLabel}</Tag>
              <Tag color="purple" style={{ fontSize: 14, fontWeight: 600 }}>{inverter.systemModeLabel}</Tag>
              <FlagTag ok={inverter.flags.running} label="RUN" />
              <FlagTag ok={inverter.flags.pllOk} label="PLL" />
              <FlagTag ok={inverter.flags.phaseOk} label="φ" />
              <FlagTag ok={inverter.flags.hericCmd} label="HERIC cmd" />
              <FlagTag ok={inverter.flags.relayHeric} label="Relay HERIC" />
              <FlagTag ok={inverter.flags.relayAc} label="Relay AC" />
              <FlagTag ok={inverter.flags.vacMatched} label="Vac match" />
            </Space>
          </Card>

          <Row gutter={[12, 12]}>
            <Col xs={12} md={6}><Metric title="PLL vd" value={fmt(inverter.pllVd, 1)} /></Col>
            <Col xs={12} md={6}><Metric title="PLL vq" value={fmt(inverter.pllVq, 4)} /></Col>
            <Col xs={12} md={6}><Metric title="Battery V" value={fmt(inverter.batteryVoltage, 2)} suffix="V" /></Col>
            <Col xs={12} md={6}><Metric title="DAB chiều" value={dab.direction} /></Col>
          </Row>
        </Space>
      ),
    },
    {
      key: 'mppt',
      label: 'MPPT',
      children: (
        <Row gutter={[12, 12]}>
          <Col xs={12} md={6}><Metric title="PV Voltage" value={fmt(mppt.pvVoltage, 1)} suffix="V" /></Col>
          <Col xs={12} md={6}><Metric title="PV Current" value={fmt(mppt.pvCurrent, 2)} suffix="A" /></Col>
          <Col xs={12} md={6}><Metric title="PV Power" value={fmt(mppt.pvPower, 1)} suffix="W" /></Col>
          <Col xs={12} md={6}><Metric title="DC Bus" value={fmt(mppt.hvBusVoltage, 1)} suffix="V" /></Col>
          <Col span={24}>
            <Alert type="info" showIcon message="SoftDebug MPPT control (duty/Vpv target) chưa đưa lên MQTT — chỉ telemetry." />
          </Col>
        </Row>
      ),
    },
    {
      key: 'pll',
      label: 'SOGI-PLL',
      children: (
        <Space direction="vertical" size={16} style={{ width: '100%' }}>
          <Row gutter={[12, 12]}>
            <Col xs={12} md={6}><Metric title="f PLL" value={fmt(pll.freqHz, 2)} suffix="Hz" /></Col>
            <Col xs={12} md={6}><Metric title="f ZC" value={fmt(pll.freqZcHz, 2)} suffix="Hz" /></Col>
            <Col xs={12} md={6}><Metric title="vd" value={fmt(pll.vd, 1)} /></Col>
            <Col xs={12} md={6}><Metric title="vq_norm" value={fmt(pll.vq, 4)} /></Col>
            <Col xs={12} md={6}><Metric title="pllDrop" value={fmt(pll.drop, 0)} /></Col>
            <Col xs={12} md={6}><Metric title="pllPending" value={fmt(pll.pending, 0)} /></Col>
          </Row>
          <Alert
            type="info"
            showIcon
            message="SoftDebug SOGI full (α/β/θ/ω) cần gói 0x8006 — MQTT hiện map từ BOARD (f, vd, vq, drop/pending)."
          />
        </Space>
      ),
    },
    {
      key: 'connection',
      label: 'Connection',
      children: (
        <Descriptions bordered column={1} labelStyle={{ fontWeight: 600 }} contentStyle={{ fontSize: 18, fontWeight: 700 }}>
          <Descriptions.Item label="Device"><BoldVal>{meta.name} ({meta.deviceId})</BoldVal></Descriptions.Item>
          <Descriptions.Item label="Model"><BoldVal>{meta.model}</BoldVal></Descriptions.Item>
          <Descriptions.Item label="STM32 link">
            {meta.stm32Link ? <Tag color="green" style={{ fontSize: 14, fontWeight: 700 }}>OK</Tag> : <Tag color="red" style={{ fontSize: 14, fontWeight: 700 }}>Lost</Tag>}
          </Descriptions.Item>
          <Descriptions.Item label="Debug MQTT 100ms">
            {meta.debugMode ? <Tag color="purple" style={{ fontSize: 14, fontWeight: 700 }}>ON</Tag> : <Tag style={{ fontSize: 14, fontWeight: 700 }}>OFF (10s)</Tag>}
          </Descriptions.Item>
          <Descriptions.Item label="Status"><BoldVal>{meta.status}</BoldVal></Descriptions.Item>
          <Descriptions.Item label="Wi‑Fi RSSI"><BoldVal>{meta.wifiRssi != null ? `${meta.wifiRssi} dBm` : '—'}</BoldVal></Descriptions.Item>
          <Descriptions.Item label="ESP temp"><BoldVal>{meta.espTemperature != null ? `${meta.espTemperature} °C` : '—'}</BoldVal></Descriptions.Item>
          <Descriptions.Item label="MCU tick"><BoldVal>{fmt(meta.mcuTickMs, 0, 'ms')}</BoldVal></Descriptions.Item>
          <Descriptions.Item label="Protocol"><BoldVal>{meta.connection?.protocol || '—'}</BoldVal></Descriptions.Item>
          <Descriptions.Item label="Connection"><BoldVal>{meta.connection?.connectionStatus || '—'}</BoldVal></Descriptions.Item>
          <Descriptions.Item label="Data updated">
            <BoldVal>{meta.updatedAt ? new Date(meta.updatedAt).toLocaleString('vi-VN') : '—'}</BoldVal>
          </Descriptions.Item>
        </Descriptions>
      ),
    },
  ];

  return <Tabs items={tabItems} destroyInactiveTabPane={false} />;
}

export default function DeviceDebug() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [devices, setDevices] = useState([]);
  const [selectedId, setSelectedId] = useState(searchParams.get('id') || undefined);
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(false);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [liveState, setLiveState] = useState('idle'); // idle | connecting | live | error | poll

  const loadDevices = useCallback(async () => {
    try {
      const res = await inverter_api.getAllInverters({ limit: 1000 });
      if (res.success) {
        setDevices(res.data || []);
      }
    } catch (error) {
      message.error(error.message || 'Không tải được danh sách thiết bị');
    }
  }, []);

  const loadDetail = useCallback(async (id, { quiet } = {}) => {
    if (!id) {
      setDetail(null);
      return;
    }
    try {
      if (!quiet) {
        setLoading(true);
      }
      const res = await inverter_api.getInverterDetail(id);
      if (res.success) {
        setDetail(res.data);
      } else if (!quiet) {
        message.error(res.message || 'Không tải được chi tiết');
      }
    } catch (error) {
      if (!quiet) {
        message.error(error.message || 'Không tải được chi tiết');
      }
    } finally {
      if (!quiet) {
        setLoading(false);
      }
    }
  }, []);

  useEffect(() => {
    loadDevices();
  }, [loadDevices]);

  useEffect(() => {
    if (selectedId) {
      loadDetail(selectedId);
      setSearchParams({ id: String(selectedId) }, { replace: true });
    } else {
      setDetail(null);
    }
  }, [selectedId, loadDetail, setSearchParams]);

  const debugOn = Boolean(detail?.debugMode || detail?.currentData?.debugMode);

  // Debug OFF → poll DB 5s. Debug ON → SSE live (MQTT forward), không poll 1s.
  useEffect(() => {
    if (!autoRefresh || !selectedId) {
      setLiveState('idle');
      return undefined;
    }

    if (!debugOn) {
      setLiveState('poll');
      const timer = setInterval(() => loadDetail(selectedId, { quiet: true }), 5000);
      return () => clearInterval(timer);
    }

    const url = buildDebugStreamUrl(selectedId);
    if (!url) {
      setLiveState('error');
      message.warning('Thiếu token — đăng nhập lại để nhận SoftDebug live');
      return undefined;
    }

    setLiveState('connecting');
    const es = new EventSource(url);

    es.addEventListener('connected', () => {
      setLiveState('live');
    });

    es.addEventListener('telemetry', (evt) => {
      try {
        const msg = JSON.parse(evt.data);
        if (!msg?.currentData) {
          return;
        }
        setLiveState('live');
        setDetail((prev) => {
          if (!prev || String(prev.id) !== String(selectedId)) {
            return prev;
          }
          return {
            ...prev,
            debugMode: msg.debugMode !== undefined ? Boolean(msg.debugMode) : prev.debugMode,
            currentData: {
              ...msg.currentData,
              debugMode: true,
            },
            connection: {
              ...(prev.connection || {}),
              connectionStatus: 'connected',
              protocol: 'mqtt',
              lastConnected: msg.at || new Date().toISOString(),
            },
            status: 'online',
          };
        });
      } catch (_err) {
        /* ignore bad frame */
      }
    });

    es.onerror = () => {
      setLiveState('error');
    };

    // Đồng bộ cờ debugMode (tắt từ trang Devices) — không ghi đè currentData live
    const metaTimer = setInterval(async () => {
      try {
        const res = await inverter_api.getInverterDetail(selectedId);
        if (!res.success || !res.data) {
          return;
        }
        setDetail((prev) => {
          if (!prev || String(prev.id) !== String(selectedId)) {
            return prev;
          }
          return {
            ...prev,
            debugMode: Boolean(res.data.debugMode),
            name: res.data.name,
            status: res.data.status,
          };
        });
      } catch (_err) {
        /* ignore */
      }
    }, 30000);

    return () => {
      es.close();
      clearInterval(metaTimer);
      setLiveState('idle');
    };
  }, [autoRefresh, selectedId, debugOn, loadDetail]);

  const view = useMemo(() => {
    if (!detail) {
      return null;
    }
    return mapSoftDebugFromMqtt(detail.currentData || {}, detail);
  }, [detail]);

  const liveBadge = (() => {
    if (!autoRefresh) {
      return <Tag>Manual</Tag>;
    }
    if (debugOn && liveState === 'live') {
      return <Badge status="processing" text={<Text strong style={{ color: '#389e0d' }}>Live MQTT</Text>} />;
    }
    if (debugOn && liveState === 'connecting') {
      return <Badge status="warning" text="Đang nối live…" />;
    }
    if (debugOn && liveState === 'error') {
      return <Badge status="error" text="Live lỗi — kiểm tra debug scope / token" />;
    }
    if (!debugOn && liveState === 'poll') {
      return <Badge status="default" text="Poll REST←RAM 5s" />;
    }
    return null;
  })();

  return (
    <div>
      <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
        <Space wrap>
          <BugOutlined style={{ fontSize: 22 }} />
          <Title level={3} style={{ margin: 0 }}>SoftDebug (MQTT)</Title>
          <Text type="secondary">Map BOARD/DAB SoftDebug ← telemetry MQTTS</Text>
          {liveBadge}
        </Space>
        <Space wrap>
          <Select
            showSearch
            allowClear
            placeholder="Chọn thiết bị"
            style={{ minWidth: 280 }}
            value={selectedId ? Number(selectedId) : undefined}
            optionFilterProp="label"
            options={devices.map((d) => ({
              value: d.id,
              label: `${d.name} · ${d.deviceId}`,
            }))}
            onChange={(val) => setSelectedId(val ? String(val) : undefined)}
          />
          <Switch
            checkedChildren="Auto"
            unCheckedChildren="Manual"
            checked={autoRefresh}
            onChange={setAutoRefresh}
          />
          <Button icon={<ReloadOutlined />} loading={loading} onClick={() => loadDetail(selectedId)}>
            Làm mới
          </Button>
          <Button icon={<ApiOutlined />} onClick={() => navigate('/devices')}>
            Danh sách
          </Button>
        </Space>
      </div>

      <Alert
        style={{ marginBottom: 16 }}
        type={debugOn ? 'success' : 'info'}
        showIcon
        icon={<ThunderboltOutlined />}
        message={
          debugOn
            ? 'Debug ON: MQTT → SSE SoftDebug (live). DB chỉ ~5 phút/lần.'
            : 'MQTT → RAM; app/web poll REST đọc RAM (~10s). DB chỉ ~5 phút/lần.'
        }
        description={
          debugOn
            ? 'SSE chỉ cho SoftDebug admin. User app không cần SSE — REST đã merge gói mới nhất từ RAM.'
            : 'Không cần SSE cho user: mỗi poll REST lấy currentData live từ bộ nhớ server (hết hạn ~90s nếu máy offline).'
        }
      />

      <Card loading={loading && !detail}>
        <SoftDebugPanel view={view} />
      </Card>
    </div>
  );
}
